import { Autocomplete, AutocompleteItem, Spinner } from '@heroui/react';
import { Search } from 'iconoir-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const WILDCARD_ITEM_KEY = '__wildcard__';

export type AsyncAutocompleteItem = {
  key: string;
  label: string;
};

export type AsyncAutocompleteSubmitEvent =
  | { type: 'typed'; value: string }
  | { type: 'exact'; key: string; label: string };

export type AsyncAutocompleteInputProps = {
  classNames?: Partial<Record<'inputWrapper' | 'label' | 'input', string>>;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
};

export type AsyncAutocompleteProps = {
  label?: string;
  placeholder?: string;
  items: AsyncAutocompleteItem[];
  isLoading?: boolean;
  hasMore?: boolean;
  value?: string | null;
  debounceMs?: number;
  emptyContent?: string;

  errorContent?: React.ReactNode;
  disableWildcard?: boolean;
  getWildcardItemLabel?: (term: string) => string;
  inputProps?: AsyncAutocompleteInputProps;
  onSearch: (query: string) => void;
  onLoadMore?: () => void;
  onSubmit?: (event: AsyncAutocompleteSubmitEvent) => void;
  onInputChange?: (value: string) => void;
  'aria-label'?: string;
  'data-testid'?: string;
};

const LABELED_INPUT_STYLES = {
  inputWrapper:
    'border-1 border-solid border-outline rounded-md hover:!border-gray-400 focus-within:!border-primary py-3.5 px-3 h-[68px]',
  label: '!text-label-md !h-4 !transform-none top-[14px] left-3',
  input: 'text-body-regular-md'
};

/** Generic async Autocomplete with debounced search, infinite scroll, and LIKE/EXACT submit. */
export function AsyncAutocomplete({
  label,
  placeholder,
  items,
  isLoading = false,
  hasMore = false,
  value,
  debounceMs = 400,
  emptyContent,
  errorContent,
  disableWildcard = false,
  getWildcardItemLabel = term => `Search for "${term}"`,
  inputProps,
  onSearch,
  onLoadMore,
  onSubmit,
  onInputChange,
  'aria-label': ariaLabel,
  'data-testid': dataTestId
}: AsyncAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const justSelectedRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    onSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value === null) {
      setInputValue('');
      setSelectedKey(null);
    } else if (typeof value === 'string') {
      const isWildcard =
        value.startsWith('*') && value.endsWith('*') && value.length > 2;
      if (isWildcard) {
        setInputValue(value.slice(1, -1));
      } else {
        setInputValue(value);
      }
      setSelectedKey(null);
    }
  }, [value]);

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node || !hasMore) return;
      observerRef.current = new IntersectionObserver(
        entries => {
          if (entries[0]?.isIntersecting) {
            onLoadMoreRef.current?.();
          }
        },
        { threshold: 0.1 }
      );
      observerRef.current.observe(node);
    },
    [hasMore]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const closingRef = useRef(false);
  const closeMenu = () => {
    const input = wrapperRef.current?.querySelector('input');
    if (!input) return;
    closingRef.current = true;
    input.blur();
    setTimeout(() => {
      closingRef.current = false;
    }, 0);
  };

  const handleInputChange = (val: string) => {
    if (closingRef.current) return;
    justSelectedRef.current = false;
    if (val === '' && inputValue !== '') {
      onSubmit?.({ type: 'typed', value: '' });
    }
    setInputValue(val);
    onInputChange?.(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearch(val);
    }, debounceMs);
  };

  const submitLike = (term: string) => {
    if (disableWildcard) {
      onSubmit?.({ type: 'typed', value: term });
    } else {
      onSubmit?.({ type: 'typed', value: `*${term}*` });
    }
    closeMenu();
  };

  const handleSelectionChange = (key: string | number | null) => {
    if (!key) return;
    const strKey = String(key);
    if (strKey === WILDCARD_ITEM_KEY) {
      const term = inputValue.trim();
      if (!term) return;
      justSelectedRef.current = true;
      setSelectedKey(null);
      submitLike(term);
      return;
    }
    const item = items.find(i => i.key === strKey);
    if (!item) return;
    justSelectedRef.current = true;
    setSelectedKey(strKey);
    setInputValue(item.label);
    closeMenu();
    onSubmit?.({ type: 'exact', key: item.key, label: item.label });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    setTimeout(() => {
      if (!justSelectedRef.current) {
        const term = inputValue.trim();
        if (term) submitLike(term);
      }
      justSelectedRef.current = false;
    }, 0);
  };

  const trimmedInput = inputValue.trim();
  const showWildcardItem = !disableWildcard && trimmedInput !== '';

  let sentinel: React.ReactNode = null;
  if (hasMore) {
    sentinel = (
      <div
        ref={sentinelRef}
        data-testid='async-autocomplete-load-more-sentinel'
        className='flex justify-center py-2'
      >
        <Spinner size='sm' />
      </div>
    );
  } else if (isLoading) {
    sentinel = (
      <div
        data-testid='async-autocomplete-loading-spinner'
        className='flex justify-center py-2'
      >
        <Spinner size='sm' />
      </div>
    );
  }

  const computedEmptyContent = isLoading ? (
    <div className='flex justify-center py-2'>
      <Spinner size='sm' />
    </div>
  ) : (
    (errorContent ?? emptyContent ?? null)
  );

  return (
    <div ref={wrapperRef} data-testid={dataTestId} className='relative'>
      <Autocomplete
        label={label}
        aria-label={ariaLabel ?? label ?? 'Search'}
        placeholder={placeholder}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        selectedKey={selectedKey}
        onSelectionChange={handleSelectionChange}
        onKeyDown={handleKeyDown}
        menuTrigger='focus'
        allowsCustomValue
        defaultFilter={() => true}
        isVirtualized={false}
        variant='bordered'
        classNames={{
          base: 'w-full',
          listboxWrapper: 'overflow-y-auto'
        }}
        endContent={inputProps?.endContent}
        inputProps={{
          startContent: inputProps?.startContent,
          classNames: {
            ...LABELED_INPUT_STYLES,
            ...(inputProps?.classNames ?? {})
          }
        }}
        listboxProps={{
          bottomContent: sentinel,
          emptyContent: computedEmptyContent
        }}
      >
        {[
          showWildcardItem ? (
            <AutocompleteItem
              key={WILDCARD_ITEM_KEY}
              textValue={getWildcardItemLabel(trimmedInput)}
              startContent={<Search className='icon-smallest' />}
              data-testid='async-autocomplete-wildcard-item'
            >
              {getWildcardItemLabel(trimmedInput)}
            </AutocompleteItem>
          ) : null,
          ...items.map(item => (
            <AutocompleteItem key={item.key} textValue={item.label}>
              {item.label}
            </AutocompleteItem>
          ))
        ]}
      </Autocomplete>
    </div>
  );
}
